// Port of TrailData/Store/TrailDataStore.swift (spatial index in spatialIndex.ts).
//
// Swift overloads that differ only by argument label get the label appended to the method name, as in the
// `GeoDataQuerying` interface of src/core/models.ts: `stages(forTrail:)` → `stagesForTrail`, `stages(near:…)` →
// `stagesNear`, `protectedAreas(containing:)` → `protectedAreasContaining`, `pointsOfInterest(ids:)` →
// `pointsOfInterestIDs`; `nearestStage(to:)` and `nearestStage(to:where:)` are one method with an optional filter.

import { protectedAreaBounds, protectedAreaContains, protectedAreaDistance, coverageCovers } from '../core/domain';
import { boundsContains, boundsExpanded, boundsIntersect, boundsUnion, distance, isValidCoordinate, polygonBounds, polygonContains } from '../core/geo';
import type {
  GeoBounds,
  GeoCoordinate,
  GeoDataQuerying,
  GeoPolygon,
  POICategory,
  PointOfInterest,
  ProtectedArea,
  Regulation,
  RegulatoryCoverage,
  TerrainSample,
  Trail,
  TrailDataset,
  TrailStage,
} from '../core/models';
import { mergeDatasets } from './merge';
import { allProvenance } from './route';
import { GridIndex, PolylineDistanceIndex, pointBounds } from './spatialIndex';

interface Hit {
  index: number;
  distance: number;
}

function firstIndexByID(items: readonly { id: string }[]): Map<string, number> {
  const map = new Map<string, number>();
  items.forEach((item, index) => {
    if (!map.has(item.id)) map.set(item.id, index);
  });
  return map;
}

function boundsOfPolygons(polygons: readonly GeoPolygon[]): GeoBounds | undefined {
  let out: GeoBounds | undefined;
  for (const p of polygons) {
    const b = polygonBounds(p);
    if (b) out = out ? boundsUnion(out, b) : b;
  }
  return out;
}

function sortedNearestFirst(hits: Hit[]): Hit[] {
  return hits.sort((a, b) => (a.distance === b.distance ? a.index - b.index : a.distance - b.distance));
}

function searchBounds(coordinate: GeoCoordinate, radiusMeters: number): GeoBounds {
  return boundsExpanded(pointBounds(coordinate), radiusMeters);
}

/** Result of `nearestStage`: the stage and its distance from the query point, metres. */
export interface NearestStage {
  stage: TrailStage;
  distance: number;
}

/**
 * Immutable snapshot of a `TrailDataset` with a uniform grid spatial index.
 *
 * Built once (O(n)) and then queried many times: POIs and terrain samples are indexed by point, protected areas by
 * bounding box, stages by chunked polyline bounds. `…Near` queries are sorted nearest-first (ties broken by dataset
 * order); other queries return records in dataset order.
 *
 * If the dataset contains duplicate ids, id lookups return the first record; use `DatasetValidator` to detect such
 * problems and `merging` to combine datasets without duplicates.
 */
export class TrailDataStore implements GeoDataQuerying {
  /** Grid cell size of the spatial index, in degrees (~5.5 km north–south, ~3.8 km east–west in the Alps). */
  static readonly gridCellDegrees = 0.05;

  /** Whether any part of the data is synthetic (metadata flag, any record provenance or synthetic flag). */
  readonly containsSyntheticData: boolean;

  private readonly trailIndexByID: Map<string, number>;
  private readonly stageIndexByID: Map<string, number>;
  private readonly poiIndexByID: Map<string, number>;
  private readonly areaIndexByID: Map<string, number>;

  private readonly stageGeometries: PolylineDistanceIndex[];
  private readonly poiGrid: GridIndex;
  private readonly areaGrid: GridIndex;
  private readonly terrainGrid: GridIndex;
  private readonly areaBounds: Array<GeoBounds | undefined>;
  private readonly coverageBounds: Array<GeoBounds | undefined>;
  /** Bounds of polygon-scoped regulations (undefined for country- and protected-area-scoped ones). */
  private readonly regulationBounds: Array<GeoBounds | undefined>;

  /** Builds the store and its spatial indexes. */
  constructor(readonly dataset: TrailDataset) {
    this.trailIndexByID = firstIndexByID(dataset.trails);
    this.stageIndexByID = firstIndexByID(dataset.stages);
    this.poiIndexByID = firstIndexByID(dataset.pointsOfInterest);
    this.areaIndexByID = firstIndexByID(dataset.protectedAreas);

    this.stageGeometries = dataset.stages.map((s) => new PolylineDistanceIndex(s.polyline));

    this.poiGrid = new GridIndex(TrailDataStore.gridCellDegrees);
    dataset.pointsOfInterest.forEach((poi, index) => this.poiGrid.insertPoint(index, poi.coordinate));

    this.areaGrid = new GridIndex(TrailDataStore.gridCellDegrees);
    this.areaBounds = dataset.protectedAreas.map((a) => protectedAreaBounds(a));
    this.areaBounds.forEach((bounds, index) => {
      if (bounds) this.areaGrid.insertBounds(index, bounds);
    });

    this.terrainGrid = new GridIndex(TrailDataStore.gridCellDegrees);
    (dataset.terrain?.samples ?? []).forEach((sample, index) => this.terrainGrid.insertPoint(index, sample.coordinate));

    this.coverageBounds = dataset.regulatoryCoverage.map((c) => boundsOfPolygons(c.polygons));
    this.regulationBounds = dataset.regulations.map((r) => {
      switch (r.scope.kind) {
        case 'administrativeArea':
        case 'area':
          return boundsOfPolygons(r.scope.polygons);
        case 'country':
        case 'protectedArea':
          return undefined;
      }
    });

    this.containsSyntheticData =
      dataset.metadata.isSynthetic ||
      allProvenance(dataset).some((p) => p.isSynthetic) ||
      dataset.regulations.some((r) => r.isSynthetic) ||
      dataset.protectedAreas.some((a) => a.isSynthetic) ||
      dataset.regulatoryCoverage.some((c) => c.isSynthetic);
  }

  /** A store over an empty, non-synthetic dataset (no data loaded yet). */
  static empty(): TrailDataStore {
    return new TrailDataStore({
      metadata: { id: 'empty', name: '', version: '0', generatedAt: 0, isSynthetic: false, provenance: [] },
      trails: [],
      stages: [],
      pointsOfInterest: [],
      protectedAreas: [],
      regulations: [],
      regulatoryCoverage: [],
    });
  }

  // MARK: Records

  /** All trails, in dataset order. */
  get trails(): Trail[] {
    return this.dataset.trails;
  }

  /** The trail with the given id. */
  trail(id: string): Trail | undefined {
    const index = this.trailIndexByID.get(id);
    return index === undefined ? undefined : this.dataset.trails[index];
  }

  /**
   * Stages of a trail in canonical order (the trail's `stageIDs`, or by stage number when the trail record is
   * missing). Swift `stages(forTrail:)`.
   */
  stagesForTrail(trailID: string): TrailStage[] {
    const trail = this.trail(trailID);
    if (!trail) {
      return this.dataset.stages.filter((s) => s.trailID === trailID).sort((a, b) => a.number - b.number);
    }
    const out: TrailStage[] = [];
    for (const id of trail.stageIDs) {
      const s = this.stage(id);
      if (s) out.push(s);
    }
    return out;
  }

  /** The stage with the given id. */
  stage(id: string): TrailStage | undefined {
    const index = this.stageIndexByID.get(id);
    return index === undefined ? undefined : this.dataset.stages[index];
  }

  /** The POI with the given id. */
  poi(id: string): PointOfInterest | undefined {
    const index = this.poiIndexByID.get(id);
    return index === undefined ? undefined : this.dataset.pointsOfInterest[index];
  }

  /** POIs for the given ids, in the order requested; unknown ids are skipped. Swift `pointsOfInterest(ids:)`. */
  pointsOfInterestIDs(ids: readonly string[]): PointOfInterest[] {
    const out: PointOfInterest[] = [];
    for (const id of ids) {
      const p = this.poi(id);
      if (p) out.push(p);
    }
    return out;
  }

  /** The protected area with the given id. */
  protectedArea(id: string): ProtectedArea | undefined {
    const index = this.areaIndexByID.get(id);
    return index === undefined ? undefined : this.dataset.protectedAreas[index];
  }

  // MARK: Stages

  /** Stages whose polyline passes within `radiusMeters` of `coordinate`, nearest first. Swift `stages(near:radiusMeters:)`. */
  stagesNear(coordinate: GeoCoordinate, radiusMeters: number): TrailStage[] {
    if (!isValidCoordinate(coordinate) || !Number.isFinite(radiusMeters) || radiusMeters < 0) return [];
    const hits: Hit[] = [];
    this.stageGeometries.forEach((geometry, index) => {
      if (geometry.lowerBoundDistance(coordinate) > radiusMeters) return;
      const d = geometry.distance(coordinate, radiusMeters);
      if (d <= radiusMeters) hits.push({ index, distance: d });
    });
    return sortedNearestFirst(hits).map((h) => this.dataset.stages[h.index]);
  }

  /**
   * The stage whose polyline is closest to `coordinate`, with that distance in metres, among the stages `isIncluded`
   * accepts (all by default; e.g. only the followed trail's stages, not those of other merged downloads).
   */
  nearestStage(coordinate: GeoCoordinate, isIncluded?: (stage: TrailStage) => boolean): NearestStage | undefined {
    if (!isValidCoordinate(coordinate)) return undefined;
    const ordered: Array<{ index: number; bound: number }> = [];
    this.stageGeometries.forEach((geometry, index) => {
      if (isIncluded && !isIncluded(this.dataset.stages[index])) return;
      const bound = geometry.lowerBoundDistance(coordinate);
      if (Number.isFinite(bound)) ordered.push({ index, bound });
    });
    ordered.sort((a, b) => (a.bound === b.bound ? a.index - b.index : a.bound - b.bound));

    let bestIndex: number | undefined;
    let bestDistance = Infinity;
    for (const candidate of ordered) {
      if (candidate.bound > bestDistance) break;
      const d = this.stageGeometries[candidate.index].distance(coordinate, bestDistance);
      if (d < bestDistance) {
        bestDistance = d;
        bestIndex = candidate.index;
      }
    }
    if (bestIndex === undefined) return undefined;
    return { stage: this.dataset.stages[bestIndex], distance: bestDistance };
  }

  // MARK: Protected areas

  /** Protected areas containing `coordinate` (holes excluded), in dataset order. Swift `protectedAreas(containing:)`. */
  protectedAreasContaining(coordinate: GeoCoordinate): ProtectedArea[] {
    if (!isValidCoordinate(coordinate)) return [];
    const out: ProtectedArea[] = [];
    for (const index of this.areaGrid.candidates(pointBounds(coordinate))) {
      const b = this.areaBounds[index];
      if (!b || !boundsContains(b, coordinate)) continue;
      const area = this.dataset.protectedAreas[index];
      if (protectedAreaContains(area, coordinate)) out.push(area);
    }
    return out;
  }

  // MARK: Combining

  /** A new store over the merged dataset: records are de-duplicated by id and `other` wins. */
  merging(other: TrailDataset): TrailDataStore {
    return new TrailDataStore(mergeDatasets(this.dataset, other));
  }

  // MARK: GeoDataQuerying

  /** POIs within `radiusMeters` (great-circle) of `coordinate`, optionally filtered by category, nearest first. */
  pointsOfInterestNear(
    coordinate: GeoCoordinate,
    radiusMeters: number,
    categories?: ReadonlySet<POICategory>,
  ): PointOfInterest[] {
    if (!isValidCoordinate(coordinate) || !Number.isFinite(radiusMeters) || radiusMeters < 0) return [];
    const hits: Hit[] = [];
    for (const index of this.poiGrid.candidates(searchBounds(coordinate, radiusMeters))) {
      const poi = this.dataset.pointsOfInterest[index];
      if (categories && !categories.has(poi.category)) continue;
      const d = distance(coordinate, poi.coordinate);
      if (d <= radiusMeters) hits.push({ index, distance: d });
    }
    return sortedNearestFirst(hits).map((h) => this.dataset.pointsOfInterest[h.index]);
  }

  /** POIs inside `bounds`, optionally filtered by category, in dataset order. */
  pointsOfInterestIn(bounds: GeoBounds, categories?: ReadonlySet<POICategory>): PointOfInterest[] {
    const out: PointOfInterest[] = [];
    for (const index of this.poiGrid.candidates(bounds)) {
      const poi = this.dataset.pointsOfInterest[index];
      if (categories && !categories.has(poi.category)) continue;
      if (boundsContains(bounds, poi.coordinate)) out.push(poi);
    }
    return out;
  }

  /** Protected areas whose boundary is within `radiusMeters` of `coordinate` (distance 0 when inside), nearest first. */
  protectedAreasNear(coordinate: GeoCoordinate, radiusMeters: number): ProtectedArea[] {
    if (!isValidCoordinate(coordinate) || !Number.isFinite(radiusMeters) || radiusMeters < 0) return [];
    const hits: Hit[] = [];
    for (const index of this.areaGrid.candidates(searchBounds(coordinate, radiusMeters))) {
      const b = this.areaBounds[index];
      if (b && PolylineDistanceIndex.lowerBoundDistance(coordinate, b) > radiusMeters) continue;
      const d = protectedAreaDistance(this.dataset.protectedAreas[index], coordinate);
      if (d <= radiusMeters) hits.push({ index, distance: d });
    }
    return sortedNearestFirst(hits).map((h) => this.dataset.protectedAreas[h.index]);
  }

  /** Protected areas whose bounding box intersects `bounds`, in dataset order. */
  protectedAreasIn(bounds: GeoBounds): ProtectedArea[] {
    const out: ProtectedArea[] = [];
    for (const index of this.areaGrid.candidates(bounds)) {
      const b = this.areaBounds[index];
      if (b && boundsIntersect(b, bounds)) out.push(this.dataset.protectedAreas[index]);
    }
    return out;
  }

  /**
   * Regulations whose scope contains `coordinate`, in dataset order, regardless of season or effective dates:
   * - `country`: a `RegulatoryCoverage` with that `countryCode` covers the point;
   * - `administrativeArea` / `area`: one of the scope polygons contains the point;
   * - `protectedArea`: the referenced protected area exists and contains the point.
   */
  regulationsApplicableAt(coordinate: GeoCoordinate): Regulation[] {
    if (!isValidCoordinate(coordinate)) return [];
    const coveredCountries = new Set(this.regulatoryCoverageAt(coordinate).map((c) => c.countryCode));
    const out: Regulation[] = [];
    this.dataset.regulations.forEach((regulation, index) => {
      const scope = regulation.scope;
      let applies: boolean;
      switch (scope.kind) {
        case 'country':
          applies = coveredCountries.has(scope.countryCode);
          break;
        case 'administrativeArea':
        case 'area': {
          const b = this.regulationBounds[index];
          applies = b !== undefined && boundsContains(b, coordinate) && scope.polygons.some((p) => polygonContains(p, coordinate));
          break;
        }
        case 'protectedArea': {
          const area = this.protectedArea(scope.id);
          applies = area !== undefined && protectedAreaContains(area, coordinate);
          break;
        }
      }
      if (applies) out.push(regulation);
    });
    return out;
  }

  /** Regulatory coverage records covering `coordinate`, in dataset order. */
  regulatoryCoverageAt(coordinate: GeoCoordinate): RegulatoryCoverage[] {
    if (!isValidCoordinate(coordinate)) return [];
    const out: RegulatoryCoverage[] = [];
    this.dataset.regulatoryCoverage.forEach((coverage, index) => {
      const b = this.coverageBounds[index];
      if (b && boundsContains(b, coordinate) && coverageCovers(coverage, coordinate)) out.push(coverage);
    });
    return out;
  }

  /** Terrain samples inside `bounds`, in dataset order. */
  terrainSamplesIn(bounds: GeoBounds): TerrainSample[] {
    const samples = this.dataset.terrain?.samples;
    if (!samples) return [];
    const out: TerrainSample[] = [];
    for (const index of this.terrainGrid.candidates(bounds)) {
      if (boundsContains(bounds, samples[index].coordinate)) out.push(samples[index]);
    }
    return out;
  }
}
